import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { translateKey } from 'src/locales/translate';
import { openVisitAttachment } from 'src/utils/openVisitAttachment';
import { getFileIcon } from '../../profile/components/ProfileFilesTable';
import { useProfileVisit } from '../hooks/useProfileVisit';
import { selectOwnerBookingDocuments } from 'src/utils/ownerBookingDocuments';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';

// ---------------------------------------------------------------------

export default function VisitDetails() {
  const { t } = useTranslation();
  const theme = useTheme();
  const data = useProfileVisit();
  const { enqueueSnackbar } = useSnackbar();

  // Re-sign on open rather than trusting the stored doc.url (short-lived signed
  // URL / not always an absolute URL).
  const openAttachment = (filename: string) => {
    if (!data?.id) return;
    openVisitAttachment({ visitId: data.id, filename }).catch(() =>
      enqueueSnackbar(
        translateKey('hardcoded.nie_udalo_sie_otworzyc_dokumentu'),
        { variant: 'error' },
      ),
    );
  };

  return (
    <Stack
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: 2,
        boxShadow: theme.customShadows.z8,
      }}
    >
      <Stack sx={CARD_HEADER_SX}>
        <Typography {...CARD_HEADER_TITLE_PROPS}>
          {t('visit.visitReasonTitle')}
        </Typography>
      </Stack>
      <Box>
        <Stack
          sx={{
            paddingX: 2,
            paddingY: 2,
            paddingTop: 1,
            gap: 2,
          }}
        >
          <Typography color="text.secondary">{data?.description}</Typography>
          {selectOwnerBookingDocuments(data?.documents as any[]).length > 0 && (
            <Stack sx={{ gap: 1 }}>
              <Typography>{t('visit.attachmentsHeading')}</Typography>
              <Stack sx={{ gap: 2 }}>
                {selectOwnerBookingDocuments(data?.documents as any[])
                  .sort((a, b) => {
                    const dateA = new Date(
                      a.uploadedAt || a.createdAt || 0,
                    ).getTime();
                    const dateB = new Date(
                      b.uploadedAt || b.createdAt || 0,
                    ).getTime();
                    return dateB - dateA;
                  })
                  .map((file, index) => (
                    <Stack
                      key={index}
                      sx={{
                        gap: 1.5,
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          padding: 1,
                          borderRadius: 1,
                          backgroundColor: theme.palette.background.neutral,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {getFileIcon(file.originalFilename || file.filename)}
                      </Box>
                      <Typography
                        sx={{
                          '&:hover': {
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          },
                        }}
                        onClick={() => openAttachment(file.filename)}
                      >
                        {file.originalFilename ||
                          file.filename.split('_').slice(2).join('_') ||
                          file.filename}
                      </Typography>
                    </Stack>
                  ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
